import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2, CalendarDays, ChevronDown, Clock, FileText, Home, ListTodo, MapPin, MapPinned, Navigation, ShoppingBasket, Sofa, Truck, User2, X } from 'lucide-react';
import ModalShell from './ModalShell';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import { prepareRoutesReturnHistory, saveRoutesNavigationContext } from '../utils/navigationContext';
import { haptic } from '../utils/vibration';
import { getYandexAddressUrl, getYandexMapsUrl, getYandexNavigatorUrl } from '../utils/yandexMaps';
import { getDgisAddressUrl, getDgisRouteUrl } from '../utils/dgisMaps';
import yaMapsIcon from '../assets/images/ya_maps___.svg';
import yandexNavigatorIcon from '../assets/images/yandex_navigator_.svg';
import dgisIcon from '../assets/images/dgis_icon.svg';

const STAGE_CONFIG = {
  pickup: {
    label: 'Доставка от клиента',
    icon: Truck,
    iconClassName: 'text-orange-500',
    badgeClassName: 'bg-orange-50 text-orange-700 dark:bg-orange-950/25 dark:text-orange-300'
  },
  delivery: {
    label: 'Доставка клиенту',
    icon: Home,
    iconClassName: 'text-green-500',
    badgeClassName: 'bg-green-50 text-green-700 dark:bg-green-950/25 dark:text-green-300'
  },
  cleaning: {
    label: 'Химчистка мебели',
    icon: Sofa,
    iconClassName: 'text-blue-500',
    badgeClassName: 'bg-blue-50 text-blue-700 dark:bg-blue-950/25 dark:text-blue-300'
  },
  task: {
    label: 'Поручение',
    icon: ListTodo,
    iconClassName: 'text-indigo-500',
    badgeClassName: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/25 dark:text-indigo-300'
  }
};

const formatTimeSlot = (timeSlot = '') => String(timeSlot || '').trim().replace(/\s*-\s*/g, ' - ');
const hideRegionPrefix = (address = '') => String(address || '').replace(/^[^,]+?\s+обл,\s*/i, '').trim();
const formatRouteDateShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    weekday: 'short'
  }).format(date).replace(',', '');
};
const PRIVILEGED_TYPES = new Set(['admin', 'dispatcher', 'developer']);
const getRouteEntityType = (item) => (item?.entity_type === 'task' ? 'task' : 'order');
const getOpenRouteHref = (item) => {
  const params = new URLSearchParams({ from: 'routes' });
  if (item?.scheduled_date_key) {
    params.set('route_date', item.scheduled_date_key);
  }
  return getRouteEntityType(item) === 'task'
    ? `#/tasks/${item.order_id}?${params.toString()}`
    : `#/order/${item.order_id}?${params.toString()}`;
};
const getRouteTitleLabel = (item) => (getRouteEntityType(item) === 'task' ? 'поручение' : 'заказ');
const getRouteUnavailableLabel = (item) => (
  getRouteEntityType(item) === 'task'
    ? 'Это поручение не назначено на вас'
    : 'Этот заказ не назначен на вас'
);
const ROUTE_STATUS_META = {
  blue: {
    label: 'Выполняется',
    dotClassName: 'bg-blue-500',
    badgeClassName: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
  },
  orange: {
    label: 'Повтор',
    dotClassName: 'bg-orange-500',
    badgeClassName: 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300'
  },
  gray: {
    label: 'Нераспределен',
    dotClassName: 'bg-slate-400',
    badgeClassName: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
  },
  green: {
    label: 'Выполнен',
    dotClassName: 'bg-emerald-500',
    badgeClassName: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
  },
  red: {
    label: 'Отменен',
    dotClassName: 'bg-red-500',
    badgeClassName: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
  }
};

const getRouteStatusTone = (item) => {
  const lifecycleStatus = String(item?.lifecycle_status || '').trim().toLowerCase();
  if (lifecycleStatus === 'cancelled') return 'red';
  if (lifecycleStatus === 'completed') return 'green';
  if (lifecycleStatus === 'revision_active') return 'orange';
  if (lifecycleStatus === 'unassigned') return 'gray';
  if (lifecycleStatus === 'active') return 'blue';

  if (item?.status === 'cancelled') return 'red';
  if (item?.status === 'completed') return 'green';
  if (item?.has_active_revision) return 'orange';
  if (!item?.assigned_to) return 'gray';
  return 'blue';
};

const getRouteStatusMeta = (item) => {
  const tone = getRouteStatusTone(item);
  return {
    tone,
    ...ROUTE_STATUS_META[tone]
  };
};

const RouteScheduleAccordion = ({
  title = 'Назначенные выезды',
  selectedDate,
  items = [],
  loading = false,
  open = false,
  onToggle,
  emptyText = 'На эту дату назначенных выездов пока нет.',
  hideOuterHeader = false,
  groupByDepartment = true,
  showDepartmentOnCard = false,
  showDepartmentBadge = false,
  showExecutorInCard = true,
  showStatusBadge = true,
  showUnassignedStatusBadge = true,
  hideRegionInAddress = false,
  currentUserId = null,
  currentUserType = 'user',
  onShowOnMap = null,
  routeContextBase = null,
  headerAction = null,
  subtitle = 'Назначенные этапы с выездом для логистики',
  activeView = 'list',
  onViewChange = null,
  renderMapContent = null
}) => {
  const titleText = title;
  const [openDepartments, setOpenDepartments] = React.useState({});
  const [selectedRouteItem, setSelectedRouteItem] = React.useState(null);
  const [mapConfirmModal, setMapConfirmModal] = React.useState(null);
  const { toast, showToast, hideToast } = useToast(3200);
  useModalBackButton(!!selectedRouteItem, () => setSelectedRouteItem(null));
  useModalBackButton(!!mapConfirmModal, () => setMapConfirmModal(null));
  const hasPrivilegedOpen = PRIVILEGED_TYPES.has(String(currentUserType || '').toLowerCase());
  const hasMapView = typeof renderMapContent === 'function';
  const currentView = hasMapView && activeView === 'map' ? 'map' : 'list';
  const setCurrentView = (view) => {
    haptic('selection');
    onViewChange?.(view);
  };
  const groupedItems = React.useMemo(() => {
    if (!groupByDepartment) {
      return [{
        key: 'all',
        departmentName: '',
        items
      }];
    }
    const groups = new Map();
    items.forEach((item) => {
      const key = item.department_id || `no-dept-${item.department_name || 'without'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          departmentName: item.department_name || 'Без подразделения',
          items: []
        });
      }
      groups.get(key).items.push(item);
    });
    return Array.from(groups.values());
  }, [items, groupByDepartment]);

  React.useEffect(() => {
    setOpenDepartments((prev) => {
      const keys = groupedItems.map((group) => group.key);
      const activeKey = keys.find((key) => prev[key]) || keys[0] || null;
      const next = {};
      keys.forEach((key) => {
        next[key] = key === activeKey;
      });
      return next;
    });
  }, [groupedItems]);

  const openInternalRoute = (href, options = {}) => {
    if (!href) return;
    const { routeContext = null } = options;

    if (routeContext) {
      saveRoutesNavigationContext(routeContext);
      prepareRoutesReturnHistory(routeContext.expandedDateKey || routeContext.selectedDateKey || '');
    }

    if (href.startsWith('#')) {
      window.location.hash = href.slice(1);
      return;
    }

    window.location.assign(href);
  };

  const renderRouteCard = (item) => {
    const stage = STAGE_CONFIG[item.stage_name] || (getRouteEntityType(item) === 'task' ? STAGE_CONFIG.task : STAGE_CONFIG.pickup);
    const StageIcon = stage.icon;
    const statusMeta = getRouteStatusMeta(item);
    const showItemsCount = getRouteEntityType(item) !== 'task' && item.stage_name === 'delivery' && Number(item.items_count || 0) > 0;
    const showDepartmentLine = getRouteEntityType(item) !== 'task' && !groupByDepartment && showDepartmentOnCard && showDepartmentBadge;
    const showRouteStatusBadge = showStatusBadge && statusMeta.tone !== 'blue'
      && (showUnassignedStatusBadge || statusMeta.tone !== 'gray');

    const displayAddress = hideRegionInAddress ? hideRegionPrefix(item.address_text || '') : (item.address_text || '');
    return (
      <div
        key={`${item.stage_name}-${item.order_id}`}
        role="button"
        tabIndex={0}
        onClick={() => {
          haptic('tap');
          setSelectedRouteItem(item);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            haptic('tap');
            setSelectedRouteItem(item);
          }
        }}
        className="cursor-pointer rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusMeta.dotClassName}`} />
          <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${stage.badgeClassName}`}>
            <StageIcon className={`h-4 w-4 ${stage.iconClassName}`} />
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 dark:bg-gray-800 dark:text-gray-200">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            {formatTimeSlot(item.time_slot) || 'Время не указано'}
          </span>
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 dark:bg-gray-800 dark:text-gray-200">
            № {item.order_number}
          </span>
          {showRouteStatusBadge && (
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${statusMeta.badgeClassName}`}>
              {item.lifecycle_label || statusMeta.label}
            </span>
          )}
          {showItemsCount && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-[11px] font-semibold text-green-700 dark:bg-green-950/30 dark:text-green-300">
              <ShoppingBasket className="h-3.5 w-3.5" />
              {item.items_count}
            </span>
          )}
        </div>

        {showDepartmentLine && (
          <div className="mt-2">
            <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700 dark:bg-violet-950/30 dark:text-violet-300">
              {item.department_name || 'Без подразделения'}
            </span>
          </div>
        )}

        {showExecutorInCard && (
          <div className="mt-2 flex items-start gap-2 text-xs leading-5 text-gray-700 dark:text-gray-200">
            <User2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="break-words">{item.assignee_name || 'Не назначен'}</span>
          </div>
        )}

        <div className="mt-2 flex items-start gap-2 text-xs leading-5 text-gray-700 dark:text-gray-200">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="break-words">{displayAddress || 'Адрес не указан'}</span>
        </div>
      </div>
    );
  };

  const selectedRouteStage = selectedRouteItem
    ? (STAGE_CONFIG[selectedRouteItem.stage_name] || (getRouteEntityType(selectedRouteItem) === 'task' ? STAGE_CONFIG.task : STAGE_CONFIG.pickup))
    : STAGE_CONFIG.pickup;
  const SelectedRouteStageIcon = selectedRouteStage.icon;
  const selectedRouteCanOpenDirectly = selectedRouteItem
    && (
      Number(selectedRouteItem.assigned_to) === Number(currentUserId)
      || hasPrivilegedOpen
    );
  const selectedRouteShowItemsCount = getRouteEntityType(selectedRouteItem) !== 'task' && selectedRouteItem?.stage_name === 'delivery' && Number(selectedRouteItem?.items_count || 0) > 0;
  const selectedRouteStatusMeta = selectedRouteItem ? getRouteStatusMeta(selectedRouteItem) : { tone: 'blue', ...ROUTE_STATUS_META.blue };
  const selectedRouteShowStatusBadge = selectedRouteStatusMeta.tone !== 'blue';
  const selectedRouteAddress = selectedRouteItem?.address_text || '';
  const selectedRouteDateLabel = selectedRouteItem ? formatRouteDateShort(selectedRouteItem.scheduled_date_key) : '';
  const selectedRouteMapsUrl = selectedRouteItem ? (getYandexMapsUrl(selectedRouteItem.address_text || '') || getYandexAddressUrl(selectedRouteItem.address_text || '')) : null;
  const selectedRouteNavigatorUrl = selectedRouteItem ? (getYandexNavigatorUrl(selectedRouteItem.address_text || '') || getYandexAddressUrl(selectedRouteItem.address_text || '')) : null;
  const selectedRouteDgisUrl = selectedRouteItem
    ? (getDgisRouteUrl({
        latitude: selectedRouteItem.markerLat ?? selectedRouteItem.latitude,
        longitude: selectedRouteItem.markerLng ?? selectedRouteItem.longitude,
        address: selectedRouteItem.address_text
      }) || getDgisAddressUrl(selectedRouteItem.address_text || ''))
    : null;

  const openRouteMapConfirm = (url, label) => {
    if (!url || url === '#') {
      showToast('Нет адреса для перехода', 'error');
      return;
    }
    haptic('open');
    setMapConfirmModal({
      url,
      label,
      address: selectedRouteAddress || 'Адрес маршрута'
    });
  };

  return (
    <div className={`overflow-hidden rounded-xl ${hideOuterHeader ? '' : 'border border-gray-200 dark:border-gray-700'}`}>
      {!hideOuterHeader && (
        <div className="flex items-stretch bg-gray-50 dark:bg-gray-800">
          <button
            type="button"
            onClick={onToggle}
            className="flex flex-1 items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{titleText}</span>
              {subtitle ? (
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {subtitle}
                </span>
              ) : null}
            </span>
            <span className="ml-3 inline-flex items-center gap-2">
              <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-blue-100 px-2 text-xs font-bold text-blue-600 dark:bg-blue-950/30 dark:text-blue-300">
                {loading ? '…' : items.length}
              </span>
              {headerAction}
              <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform dark:text-gray-300 ${open ? 'rotate-180' : ''}`} />
            </span>
          </button>
        </div>
      )}

      <AnimatePresence initial={false}>
        {(hideOuterHeader || open) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 bg-slate-50 p-3 dark:bg-gray-950">
              {hasMapView && (
                <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => setCurrentView('list')}
                    className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors ${
                      currentView === 'list'
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <Truck className="h-4 w-4" />
                    Список
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentView('map')}
                    className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors ${
                      currentView === 'map'
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <MapPinned className="h-4 w-4" />
                    Карта
                  </button>
                </div>
              )}

              {currentView === 'map' ? (
                renderMapContent()
              ) : loading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-400">
                  Загружаем назначенные выезды...
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-400">
                  {emptyText}
                </div>
              ) : (
                groupedItems.map((group) => (
                  <div key={group.key} className="space-y-2">
                    {groupByDepartment && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenDepartments((prev) => {
                            const willOpen = !prev[group.key];
                            const next = {};
                            groupedItems.forEach((entry) => {
                              next[entry.key] = willOpen && entry.key === group.key;
                            });
                            return next;
                          });
                        }}
                        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{group.departmentName}</span>
                        </span>
                        <span className="ml-3 inline-flex items-center gap-2">
                          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gray-200 px-1.5 text-[11px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                            {group.items.length}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform dark:text-gray-300 ${openDepartments[group.key] ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                    )}

                    <AnimatePresence initial={false}>
                      {(!groupByDepartment || openDepartments[group.key]) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-2">
                            {group.items.map(renderRouteCard)}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedRouteItem && (
        <ModalShell
          open={Boolean(selectedRouteItem)}
          onClose={() => {
            haptic('close');
            setSelectedRouteItem(null);
          }}
          variant="window"
          panelClassName="w-full max-w-md rounded-[26px] p-4 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${selectedRouteStatusMeta.dotClassName}`} />
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${selectedRouteStage.badgeClassName}`}>
                    <SelectedRouteStageIcon className="h-4 w-4" />
                  </span>
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:bg-gray-800 dark:text-gray-200">
                    № {selectedRouteItem.order_number}
                  </span>
                  {selectedRouteShowStatusBadge && (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${selectedRouteStatusMeta.badgeClassName}`}>
                      {selectedRouteItem.lifecycle_label || selectedRouteStatusMeta.label}
                    </span>
                  )}
                  {selectedRouteShowItemsCount && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700 dark:bg-green-950/30 dark:text-green-300">
                      <ShoppingBasket className="h-3.5 w-3.5" />
                      {selectedRouteItem.items_count}
                    </span>
                  )}
                </div>

                <div className="mt-3 space-y-2 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex flex-wrap items-center gap-3">
                    {selectedRouteDateLabel && (
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {selectedRouteDateLabel}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {formatTimeSlot(selectedRouteItem.time_slot) || 'Время не указано'}
                    </span>
                  </div>

                  {getRouteEntityType(selectedRouteItem) !== 'task' ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5" />
                        {selectedRouteItem.department_name || 'Подразделение не указано'}
                      </span>
                    </div>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 ${
                      selectedRouteItem.assignee_name ? '' : 'rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      <User2 className="h-3.5 w-3.5" />
                      {selectedRouteItem.assignee_name || 'Не назначен'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  haptic('tap');
                  setSelectedRouteItem(null);
                }}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Закрыть карточку маршрута"
                style={{ minHeight: 0 }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 flex items-start gap-2 rounded-2xl bg-slate-50 px-3 py-3 text-sm text-gray-800 dark:bg-gray-800/80 dark:text-gray-100">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <span className="break-words leading-5">{selectedRouteAddress || 'Адрес не указан'}</span>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {selectedRouteCanOpenDirectly ? (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      openInternalRoute(getOpenRouteHref(selectedRouteItem), {
                        routeContext: {
                          ...(routeContextBase || {}),
                          selectedDateKey: selectedRouteItem.scheduled_date_key || selectedDate || '',
                          expandedDateKey: selectedDate || selectedRouteItem.scheduled_date_key || '',
                          focusOrderId: selectedRouteItem.order_id,
                          focusEntityType: getRouteEntityType(selectedRouteItem),
                          focusRouteKey: `${getRouteEntityType(selectedRouteItem)}:${selectedRouteItem.order_id}`
                        }
                      });
                    }}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700"
                    title={`Открыть ${getRouteTitleLabel(selectedRouteItem)}`}
                    aria-label={`Открыть ${getRouteTitleLabel(selectedRouteItem)}`}
                    style={{ minHeight: 0 }}
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                      type="button"
                      onClick={() => {
                        haptic('error');
                        showToast(getRouteUnavailableLabel(selectedRouteItem), 'error');
                      }}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:hover:bg-gray-700"
                      title={getRouteUnavailableLabel(selectedRouteItem)}
                      aria-label={getRouteUnavailableLabel(selectedRouteItem)}
                      style={{ minHeight: 0 }}
                    >
                    <FileText className="h-4 w-4 opacity-55" />
                  </button>
                )}

              </div>

              <div className="flex items-center gap-2">
                {onShowOnMap && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      onShowOnMap(selectedRouteItem);
                      setSelectedRouteItem(null);
                    }}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                    title="Показать на карте"
                    aria-label="Показать на карте"
                    style={{ minHeight: 0 }}
                  >
                    <MapPinned className="h-4 w-4" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => openRouteMapConfirm(selectedRouteMapsUrl, 'Яндекс.Карты')}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                  title="Открыть в Яндекс.Картах"
                  aria-label="Открыть в Яндекс.Картах"
                  style={{ minHeight: 0 }}
                >
                  <img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" />
                </button>

                <button
                  type="button"
                  onClick={() => openRouteMapConfirm(selectedRouteNavigatorUrl, 'Яндекс.Навигатор')}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                  title="Открыть в Яндекс.Навигаторе"
                  aria-label="Открыть в Яндекс.Навигаторе"
                  style={{ minHeight: 0 }}
                >
                  <img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" />
                </button>

                {selectedRouteDgisUrl && (
                  <button
                    type="button"
                    onClick={() => openRouteMapConfirm(selectedRouteDgisUrl, '2ГИС')}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                    title="Открыть в 2ГИС"
                    aria-label="Открыть в 2ГИС"
                    style={{ minHeight: 0 }}
                  >
                    <img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" />
                  </button>
                )}
              </div>
            </div>
        </ModalShell>
      )}

      {mapConfirmModal && (
        <ModalShell
          open={Boolean(mapConfirmModal)}
          onClose={() => {
            haptic('close');
            setMapConfirmModal(null);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
              {mapConfirmModal.label === 'Яндекс.Навигатор' ? (
                <Navigation className="h-5 w-5" />
              ) : (
                <MapPin className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Открыть {mapConfirmModal.label}?
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                {mapConfirmModal.address || 'Адрес маршрута'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('open');
                window.open(mapConfirmModal.url, '_blank', 'noopener,noreferrer');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Открыть
            </button>
          </div>
        </ModalShell>
      )}

          </div>
  );
};

export default RouteScheduleAccordion;
